import { useCallback, useContext, useMemo } from "react";
import { GetMarkets200Response, InstrumentTypeResponse } from "../../codegen-api";
import { MarketInstrumentContext } from "../../contexts/MarketInstrumentContext";
import { AssetResponse } from "../../utils/asset";
import {
  getAssetFromSymbol,
  getContractPriceStep,
  IContractPriceStep,
} from "../../utils/instruments";

type InstrumentLike = {
  instrument_id?: string | number;
  instrument_name?: string;
  instrument_type?: InstrumentTypeResponse;
  asset?: string;
  underlying_asset?: string;
};

const normalize = (value?: string | number) =>
  value === undefined || value === null ? "" : String(value).toLowerCase();

const hasUsableInstrumentName = (name?: string) => {
  const normalized = normalize(name);
  return normalized !== "" && normalized !== "unknown" && normalized !== "perp";
};

export const useInstrumentResolver = () => {
  const {
    activeOptionMarkets,
    activePerpMarkets,
    activeSpotMarkets,
    getMarketPrecision,
  } = useContext(MarketInstrumentContext);

  const allMarkets = useMemo(
    () => [
      ...(activeOptionMarkets || []),
      ...(activePerpMarkets || []),
      ...(activeSpotMarkets || []),
    ],
    [activeOptionMarkets, activePerpMarkets, activeSpotMarkets]
  );

  const getTypedMarkets = useCallback(
    (instrument?: InstrumentLike) => {
      if (instrument?.instrument_type === InstrumentTypeResponse.Option) {
        return activeOptionMarkets || [];
      }
      if (instrument?.instrument_type === InstrumentTypeResponse.Spot) {
        return activeSpotMarkets || [];
      }
      if (instrument?.instrument_type === InstrumentTypeResponse.Perpetual) {
        return activePerpMarkets || [];
      }
      return allMarkets;
    },
    [activeOptionMarkets, activePerpMarkets, activeSpotMarkets, allMarkets]
  );

  const resolveMarket = useCallback(
    (instrument?: InstrumentLike): GetMarkets200Response | undefined => {
      if (!instrument) {
        return undefined;
      }

      const instrumentId = normalize(instrument.instrument_id);
      const instrumentName = normalize(instrument.instrument_name);
      const asset = normalize(
        instrument.asset ||
          instrument.underlying_asset ||
          getAssetFromSymbol(instrument.instrument_name || "")
      );
      const typedMarkets = getTypedMarkets(instrument);

      const byIdOrName = (market: GetMarkets200Response) =>
        (instrumentId && normalize(market.instrument_id) === instrumentId) ||
        (instrumentName && normalize(market.instrument_name) === instrumentName);

      return (
        typedMarkets.find(byIdOrName) ||
        allMarkets.find(byIdOrName) ||
        (asset
          ? typedMarkets.find(
              (market) => normalize(market.underlying_asset) === asset
            )
          : undefined)
      );
    },
    [allMarkets, getTypedMarkets]
  );

  const resolveInstrumentName = useCallback(
    (instrument?: InstrumentLike) => {
      const market = resolveMarket(instrument);
      if (market?.instrument_name) {
        return market.instrument_name;
      }
      return hasUsableInstrumentName(instrument?.instrument_name)
        ? instrument?.instrument_name
        : undefined;
    },
    [resolveMarket]
  );

  const resolveInstrumentAsset = useCallback(
    (instrument?: InstrumentLike): AssetResponse | undefined => {
      const market = resolveMarket(instrument);
      return (
        market?.underlying_asset ||
        instrument?.asset ||
        instrument?.underlying_asset ||
        getAssetFromSymbol(instrument?.instrument_name || "")
      );
    },
    [resolveMarket]
  );

  const resolveMarketPrecision = useCallback(
    (instrument?: InstrumentLike): IContractPriceStep => {
      const market = resolveMarket(instrument);
      if (market) {
        return getContractPriceStep(market);
      }
      return getMarketPrecision(
        resolveInstrumentAsset(instrument),
        instrument?.instrument_type
      );
    },
    [getMarketPrecision, resolveInstrumentAsset, resolveMarket]
  );

  return {
    resolveMarket,
    resolveInstrumentName,
    resolveInstrumentAsset,
    resolveMarketPrecision,
  };
};
